// plugins/msal.client.ts
// .client.ts → jamais exécuté côté serveur (window/sessionStorage safe)
//
// Rôle : initialiser MSAL et exposer l'instance via $msalReady.
// Chargement différé sur les pages publiques (perf homepage / LCP).
// handleRedirectPromise() est géré dans auth-init.client.ts et pages/login.vue
// pour éviter les conflits de routing avec Nuxt + i18n.

import type { PublicClientApplication } from '@azure/msal-browser'

export default defineNuxtPlugin(() => {
  const msalReady = (async (): Promise<PublicClientApplication | null> => {
    if (!import.meta.client) return null

    const path = useRouter().currentRoute.value.path
    const authPath =
      path === '/login' ||
      path === '/register' ||
      path.endsWith('/login') ||
      path.endsWith('/register')

    if (!authPath) {
      await new Promise<void>((resolve) => {
        if (document.readyState === 'complete') {
          resolve()
        } else {
          window.addEventListener('load', () => resolve(), { once: true })
        }
      })
      await new Promise<void>((resolve) => {
        if (typeof requestIdleCallback === 'function') {
          requestIdleCallback(() => resolve(), { timeout: 5000 })
        } else {
          setTimeout(resolve, 2000)
        }
      })
    }

    const config = useRuntimeConfig()

    const { PublicClientApplication } = await import('@azure/msal-browser')

    const msalInstance = new PublicClientApplication({
      auth: {
        clientId: config.public.msalClientId,
        authority: `https://${config.public.msalAuthority}/${config.public.msalTenantId}`,
        knownAuthorities: [config.public.msalAuthority],
        redirectUri: window.location.origin + '/login',
        postLogoutRedirectUri: window.location.origin + '/',
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    })

    await msalInstance.initialize()
    return msalInstance
  })()

  return {
    provide: {
      msalReady,
      logout: async (): Promise<void> => {
        const { useAuth } = await import('~/composables/useAuth')
        await useAuth().logout()
      },
    },
  }
})
